import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from 'components/ui/Header';
import Button from 'components/ui/Button';
import ProgressIndicator from './components/ProgressIndicator';
import WorkAreaSection from './components/WorkAreaSection';
import VatSection from './components/VatSection';
import DocumentSection from './components/DocumentSection';
import { useAuth } from '../../contexts/AuthContext';
import professionalRegistrationService from '../../services/professionalRegistrationService';
import { AlertCircle, Loader2 } from 'lucide-react';
import Footer from '../landing-page/components/Footer';

const ProfessionalRegistrationStep2 = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    primaryWorkAreas: [],
    extendedAvailability: {
      enabled: false,
      allItaly: false,
      includeSardiniaSicily: false
    },
    vatNumber: '',
    documentType: '',
    documentFile: null
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Validate form whenever formData changes
  useEffect(() => {
    const isValid = professionalRegistrationService?.validateStep2Completion(formData);
    setIsFormValid(isValid);
  }, [formData]);

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (!user?.id) {
        throw new Error('Utente non autenticato');
      }

      const result = await professionalRegistrationService?.saveStep2Data(formData, user?.id);

      if (!result?.success) {
        throw new Error(result?.error || 'Errore durante il salvataggio');
      }

      // Success - redirect to payment verification
      navigate('/payment-verification');
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitError(error?.message || 'Si è verificato un errore. Riprova.');
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundImage: 'url(/assets/images/italian_apartment_interior_step2_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'scroll',
        backgroundColor: '#f9fafb',
      }}
    >
      {/* 75% white overlay */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(255,255,255,0.75)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      {/* All page content sits above the overlay */}
      <div className="relative flex flex-col min-h-screen" style={{ zIndex: 1 }}>
        <Header />

        {/* Branded header section */}
        <div style={{ backgroundColor: '#2780b5' }} className="w-full">
          <div className="flex flex-col items-center justify-center text-center" style={{ paddingTop: '32px', paddingBottom: '32px' }}>
            <h1 className="text-white text-2xl font-semibold mb-1">Completa il tuo profilo</h1>
            <p className="text-white text-sm mb-4" style={{ opacity: 0.9 }}>Passo 2 di 3 — Ci siamo quasi</p>
            {/* Progress bar */}
            <div className="w-64 h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#32b3e5' }}>
              <div className="h-full rounded-full" style={{ width: '50%', backgroundColor: '#ffffff' }}></div>
            </div>
          </div>
        </div>
        
        <div className="flex-1 max-w-3xl mx-auto px-4 py-8 sm:px-6 lg:px-8 w-full">
          <ProgressIndicator currentStep={2} totalSteps={3} />
          
          <div className="mt-8 space-y-6">
            <WorkAreaSection 
              workArea={formData?.workArea}
              worksInAllItaly={formData?.worksInAllItaly}
              onChange={handleChange}
            />
            
            <VatSection 
              vatNumber={formData?.vatNumber}
              onChange={handleChange}
            />
            
            <DocumentSection 
              documentType={formData?.documentType}
              documentFile={formData?.documentFile}
              onChange={handleChange}
            />
          </div>
          
          {/* Error message */}
          {submitError && (
            <div className="mt-6 flex items-start gap-3 text-sm text-red-700 bg-red-50 px-4 py-3 rounded-lg border border-red-200">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Errore durante il salvataggio</p>
                <p className="mt-1">{submitError}</p>
              </div>
            </div>
          )}
          
          <div className="mt-8">
            <Button 
              fullWidth 
              size="lg"
              disabled={!isFormValid || isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Salvataggio in corso...
                </span>
              ) : (
                'Continua'
              )}
            </Button>
            
            {!isFormValid && (
              <p className="mt-3 text-sm text-gray-600 text-center">
                Completa tutti i campi per continuare
              </p>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default ProfessionalRegistrationStep2;